<script setup lang="ts">
  import Hls from "hls.js"

  const { $api } = useNuxtApp()
  const img = useImage()

  const { path, autoplay } = defineProps({
    poster: String,
    title: String,
    tags: {
      type: Array<String>,
      default: [],
    },

    path: {
      type: String,
      required: true,
    },
    rounded: {
      type: String,
      default: "rounded-3xl",
    },
    border: {
      type: String,
      required: false,
      default: "",
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    hoverEffect: Boolean,
    size:{
      type: Number,
      default: 450,
    }
  })
  const video = useTemplateRef("video")
  const isStart = ref<boolean>(false)
  const loading = ref<boolean>(false)
  const stream = ref<Hls | null>(null)

  const isStreamer = computed<boolean>(() => {
    return /\.m3u8/.test(path)
  })
  const isUUID = computed<boolean>(() => {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      path
    )
  })

  const startStreaming = (link: string) => {
    isStart.value = true

    nextTick(() => {
      if (video.value) {
        video.value.src = link

        initStreamer()
      }
    })
  }

  const fetchLink = async () => {
    loading.value = true

    if (isStreamer.value === true) {
      startStreaming(path)
    } else if (isUUID.value === true) {
      try {
        const res = await $api<{ link: string }>(`/edu/playback`, { query: { uuid: path } })

        startStreaming(res.link)
      } catch (e) {}
    } else {
      isStart.value = true

      nextTick(() => {
        if (video.value) {
          video.value.src = path
          video.value.controls = true
          
          loading.value = false
          video.value.play()
        }
      })
    }
  }

  const initStreamer = () => {
    try {
      if (!video.value) return false
      if (Hls.isSupported()) {
        const hls = new Hls()
        hls.loadSource(video.value.src)
        hls.attachMedia(video.value)

        video.value.currentTime = 0
        video.value.controls = true

        hls.on(Hls.Events.ERROR, (e, data) => {
          if (!data.fatal) return

          loading.value = false 
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError()
              break
            default:
              hls.destroy()
              break
          }
        })

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setTimeout(() => {
            loading.value = false
            video.value?.play()
          }, 300);
        })

        stream.value = hls
      } else if (video.value?.canPlayType("application/vnd.apple.mpegurl") !== "") {
        video.value?.play()
      }
    } catch (e) {
      
    }
  }

  onBeforeRouteUpdate(async () => {
    await video.value?.pause()

    if (stream.value) {
      await stream.value.stopLoad()
      stream.value.destroy()
    }
  })

  onMounted(() => {
    if (autoplay) {
      fetchLink()
    }
  })

  onUnmounted(() => {
    video.value?.pause()
    if (stream.value) {
      stream.value.stopLoad()
      stream.value.destroy()
    }
  })
</script>

<template>
  <div
    :class="[
      'aspect-video text-center max-h-80 overflow-hidden relative',
      rounded,
      border,
      { group: hoverEffect },
    ]"
  >
    <span
      v-if="isStart === false && !loading"
      :style="{ backgroundImage: `url('${img(poster as string, { width: size })}')` }"
      :class="[
        'aspect-video inline-flex items-center justify-center cursor-pointer bg-bottom bg-cover w-full h-full align-middle bg-no-repeat',
        rounded
      ]"
      @click="fetchLink"
    >
      <slot name="play-button">
        <svg class="size-13 sm:size-16" width="103" height="103" viewBox="0 0 103 103" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="51.5" cy="51.5" r="51.5" fill="#EB923C"/>
                  <g clip-path="url(#clip0_17331_568)">
                    <path d="M67.015 46.5883C67.8155 47.014 68.4851 47.6495 68.952 48.4266C69.4189 49.2038 69.6656 50.0934 69.6656 51C69.6656 51.9066 69.4189 52.7962 68.952 53.5734C68.4851 54.3505 67.8155 54.986 67.015 55.4117L45.6617 67.0233C42.2233 68.895 38 66.4617 38 62.6133V39.3883C38 35.5383 42.2233 33.1067 45.6617 34.975L67.015 46.5883Z" stroke="white" stroke-width="1.5"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_17331_568">
                  <rect width="40" height="40" fill="white" transform="translate(33 31)"/>
                  </clipPath>
                  </defs>
                </svg>
      </slot>
    </span>

    <div
      v-if="loading"
      class=""
      :class="[
        'absolute inset-0 flex items-center justify-center bg-black/60 z-10',
        rounded
      ]"
    >
      <Loading color="#FFFFFF" :size="70" />
    </div>

    <video
      v-if="isStart"
      ref="video"
      :poster="poster"
      :class="[
        'aspect-video w-full h-ful rounded-3xl bg-black inline align-middle',
        rounded
      ]"
      preload="metadata"
      autoplay
      playsinline
    ></video>
  </div>
</template>
